import './PointOfSaleCoverage.scss';
import {
  Box,
  useMediaQuery,
  useTheme,
  Typography,
  Button
} from '@mui/material';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from 'react-simple-maps';
import { geoCentroid } from 'd3-geo';
import { feature } from 'topojson-client';
import {
  SMALL_COUNTRY_CODES
} from '../../constants/posCoverage';
import { MARKET_TYPE } from '../../constants/common';
import { useEffect, useState, useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';
import AppAutocomplete from '../AppAutocomplete/AppAutocomplete';
import AppTooltip from '../AppTooltip/AppTooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Markets from './Markets/Markets';
import RemoveIcon from '@mui/icons-material/Remove';
import worldmapData from '../../assets/worldmapData.json';
import { useTranslation } from 'react-i18next';
import { visuallyHidden } from '@mui/utils';

// Jira Reference: https://aircanada.atlassian.net/browse/CRDP-55163
// As part of CRDP-55163, we converted the shared Excel sheet to worldmapData.json and added the arcs from the legacy file at src/assets/worldmap.json.
// Retaining legacy worldmap.json file for future references.

function PointOfSaleCoverage({ data }) {
  const mapRef = useRef<HTMLDivElement | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { i18n, t } = useTranslation();

  const [center, setCenter] = useState<[number, number]>([0, 0]);
  const [countryCodeMapping, setCountryCodeMapping] = useState([] as any);
  const [countries, setCountries] = useState([]);
  const [countryCodes, setCountryCode] = useState('');
  const [geoData, setGeoData] = useState(null as any);
  const [searchedMarketCode, setSearchedMarketCode] = useState<string | null>(
    ''
  );
  const [searchValue, setSearchValue] = useState<string>('');
  const [announcement, setAnnouncement] = useState<string | null>('');
  const [supportedMarkets, setSupportedMarkets] = useState(
    data.supportedMarkets.market_types
      .map((marketType) => marketType.markets)
      .flat()
      .sort((a, b) => a.name.localeCompare(b.name))
  );
  const [upcomingMarkets, setUpcomingMarkets] = useState(
    data.upcomingMarkets.markets.sort((a, b) => a.name.localeCompare(b.name))
  );
  const [zoomLevel, setZoomLevel] = useState(1);
  const [delayAccessibility, setDelayAccessibility] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setDelayAccessibility(true);

      return () => setDelayAccessibility(false);
    }, 1000);

  }, []);

  const MAP_HEIGHT = 420;
  const MAP_WIDTH = 800;
  const MAX_ZOOM = 80;

  const supportedMarketCodes = data.supportedMarkets.market_types
    .map((marketType) => marketType.markets)
    .flat()
    .map((m) => m.code);
  const upcomingMarketCodes = data.upcomingMarkets.markets.map((m) => m.code);

  const getCountryArea = async (countryCode: string) => {
    try {
      const response = await fetch(
        `https://restcountries.com/v3.1/alpha/${countryCode}`
      );
      const responseJson = await response.json();
      if (responseJson && responseJson.length > 0) {
        return responseJson[0].area;
      } else {
        console.error('Country not found or no area data available');
      }
    } catch (error) {
      console.error('Error fetching country area:', error);
    }
  };

  const normalizeSearchText = (value: string = '') => value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’`´]/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const getCountryCode = (countryName, countries, defaultValue = '') => {
    const normalizedCountryName = normalizeSearchText(countryName || '');
    for (let country of countries) {
      const code = Object.keys(country)[0];
      if (normalizeSearchText(country[code]) === normalizedCountryName) {
        return code;
      }
    }
    return defaultValue;
  };

  const getSubtitleByCountryCode = (marketTypes, countryCode: string) => {
    for (const item of marketTypes) {
      for (const market of item.markets) {
        if (market.code === countryCode) {
          return item.title;
        }
      }
    }
    return 'Country code not found';
  };

  const getZoomLevel = (area: number | null) => {
    if (!area) {
      return 1;
    }
    switch (true) {
      case area > 15000000:
        return 2.5;
      case area > 5000000:
        return 3;
      case area > 2000000:
        return 4;
      case area > 1500000:
        return 5;
      case area > 1000000:
        return 6;
      case area > 750000:
        return 7;
      case area > 250000:
        return 8;
      case area > 150000:
        return 9;
      case area > 100000:
        return 10;
      case area > 50000:
        return 11;
      case area > 25000:
        return 12;
      case area > 15000:
        return 13;
      case area > 10000:
        return 14;
      case area > 7500:
        return 20;
      case area > 5000:
        return 26;
      case area > 2500:
        return 32;
      case area > 1000:
        return 38;
      case area > 750:
        return 50;
      case area > 500:
        return 62;
      case area > 1:
        return MAX_ZOOM;
      default:
        return 1;
    }
  };

  const handleOnChangeAutocomplete = async (event, val?, reason?) => {
    if (reason === 'clear') {
      setSearchValue('');
      setAnnouncement('');
      return;
    }
    const value = val ?? event.target.textContent;
    setSearchValue((prev) => value);
    // keep focus behaviour unchanged
    setTimeout(() => {
      if (mapRef.current) {
        // mapRef.current.focus();
      }
    }, 100);
  };

  const handleInputChange = (event, val) => {
    setSearchValue(val);
  };
  const handleKeyDown = async (e) => {
    if (e.key === 'Enter') {

      await handleClick();
    }
  };
  const handleButtonKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();

      await handleClick();
    }
  };

  const handleClick = async () => {
    const countryCode = getCountryCode(searchValue, countryCodeMapping);
    if (countryCode) {
      setCountryCode(countryCode);
      const countryArea = await getCountryArea(countryCode);
      setZoomLevel(getZoomLevel(countryArea));
    }

    const updateMarkets = (markets, value) => {
      const normalizedSearchValue = normalizeSearchText(value || '');
      return markets
        .sort((a, b) => a.name.localeCompare(b.name))
        .filter((m) => {
          const nameToCheck = m.code === 'IND'
            ? m.name.split(' ')[0]
            : m.name;
          return normalizeSearchText(nameToCheck).includes(normalizedSearchValue);
        });
    };

    if (!searchValue) {
      const allSupportedMarkets = data.supportedMarkets.market_types.flatMap(
        (marketType) => marketType.markets
      );

      setSupportedMarkets(updateMarkets(allSupportedMarkets, ''));
      setUpcomingMarkets(updateMarkets(data.upcomingMarkets.markets, ''));
      setSearchedMarketCode('');
      setCenter([0, 0]);
      setZoomLevel(1);
    } else {
      const allSupportedMarkets = data.supportedMarkets.market_types.flatMap(
        (marketType) => marketType.markets
      );

      // compute updated lists locally so message uses the fresh values immediately
      const updatedSupported = updateMarkets(allSupportedMarkets, searchValue);
      const updatedUpcoming = updateMarkets(data.upcomingMarkets.markets, searchValue);

      setSupportedMarkets(updatedSupported);
      setUpcomingMarkets(updatedUpcoming);

      const countryFeature = geoData.features.find(
        (feature) => feature.properties.countryCode === (countryCode || countryCodes)
      );

      if (countryFeature) {
        const centroid = geoCentroid(countryFeature);
        setCenter([centroid[0], centroid[1] - 15]);
        if (countryCode === 'CAN' || countryCode === 'GRL') {
          setCenter([centroid[0], centroid[1] - 15]);
        } else if (countryCode === 'RUS') {
          setCenter([centroid[0], centroid[1] - 25]);
        } else if (countryCode === 'KIR') {
          setCenter([centroid[0] + 10, centroid[1] + 0.5]);
        } else {
          setCenter(centroid);
        }
      }

      // build announcement synchronously using updated lists to avoid stale state
      const normalizedSearchValue = normalizeSearchText(searchValue || '');
      const checkCountry = countries.filter((item: any) => {
        return normalizeSearchText(item) === normalizedSearchValue;
      });

      let msg = '';
      if (updatedSupported.length === 0 && updatedUpcoming.length === 0 && checkCountry.length === 1) {
        msg = `${data.search_text}`;
      } else if (updatedSupported.length === 0 && updatedUpcoming.length === 0 && checkCountry.length === 0) {
        msg = `${data.error}`;
      } else if (updatedSupported.length === 1 && updatedUpcoming.length === 0 && checkCountry.length === 0) {
        msg = `${data.error}`;
      } else if (updatedSupported.length === 0 && updatedUpcoming.length === 1 && checkCountry.length === 1) {
        msg = `${searchValue} is an upcoming market`;
      } else if (updatedSupported.length === 1 && updatedUpcoming.length === 0 && checkCountry.length === 1) {
        msg = `${searchValue} is a supported market`;
      } else if (searchValue === 'uni') {
        msg = `${data.error}`;
      }
      else {
        msg = '';
      }

      setAnnouncement('');
      setTimeout(() => { setAnnouncement(msg); }, 100);

      setSearchedMarketCode(countryCode || countryCodes);
    }

  };

  const handleZoomIn = () => {
    if (zoomLevel < MAX_ZOOM) {
      setZoomLevel((prevZoomLevel) => prevZoomLevel + 1);
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel > 1) {
      setZoomLevel((prevZoomLevel) => prevZoomLevel - 1);
    }
  };

  const renderSearchText = () => {
    const normalizedSearchValue = normalizeSearchText(searchValue || '');
    const checkCountry = countries.filter((item: any) => {
      return normalizeSearchText(item) === normalizedSearchValue;
    });
    if (supportedMarkets.length === 0 && upcomingMarkets.length === 0 && checkCountry.length === 1) {
      return (
        <Typography className="label-search">{data.search_text}</Typography>
      );
    } else if (supportedMarkets.length === 0 && upcomingMarkets.length === 0 && checkCountry.length === 0) {
      return (
        <Typography className="label-search">{data.error}</Typography>
      );
    } else if (supportedMarkets.length === 1 && upcomingMarkets.length === 0 && checkCountry.length === 0) {
      return (
        <Typography className="label-search">{data.error}</Typography>
      );
    }

    const isSupportedMarket = supportedMarkets.length !== 0;
    const marketData = isSupportedMarket
      ? {
        ...data.supportedMarkets,
        markets: data.supportedMarkets.market_types
          .map((marketType) => marketType.markets)
          .flat()
      }
      : data.upcomingMarkets;
    const market = marketData.markets.find((m) => {
      const nameToCompare = m.code === 'IND' ? m.name.split(' ')[0] : m.name;
      return normalizeSearchText(nameToCompare) === normalizedSearchValue;
    });

    return (
      <>
        {market ?
          <Typography className="label-search">
            {searchValue} {marketData.search_text} ({marketData.supporting_text}{' '}
            {market?.tooltip}).
            {isSupportedMarket && (
              <Box alignItems="center" className="container-text" display="flex">
                <CheckCircleIcon
                  aria-label={String(t('PointOfSaleCoverage.success'))}
                  className="icon-success"
                  style={{ color: 'green' }} />
                <Typography className="label-text">
                  {getSubtitleByCountryCode(marketData.market_types, market.code)}
                </Typography>
              </Box>
            )}
          </Typography> : ""}
      </>
    );
  };

  const renderTooltipTitle = (geo) => {
    const countryCode = geo.properties.countryCode; const countryName = i18n.language === 'fr'
      ? geo.properties.nameFr || geo.properties.name
      : geo.properties.nameEn || geo.properties.name;

    let market: any = null;
    let supportingText = data.unsupported_text;

    if (supportedMarketCodes.includes(countryCode)) {
      market = supportedMarkets.find((m) => m.code === countryCode);
      supportingText = data.supportedMarkets.supporting_text;
    } else if (upcomingMarketCodes.includes(countryCode)) {
      market = upcomingMarkets.find((m) => m.code === countryCode);
      supportingText = data.upcomingMarkets.supporting_text;
    }

    return (
      <Box textAlign="center">
        <Typography className="bold fs-12">{countryName}</Typography>
        <Typography className="capitalize fs-12">
          {supportingText}
          {market && `: ${market.tooltip}`}
        </Typography>
      </Box>
    );
  };

  const styleFunction = (countryCode: string) => {
    const baseStyles = {
      fillColor: '#C1C5C8',
      strokeColor: '#FFFFFF',
      strokeWidth: '0.4px'
    };

    if (searchedMarketCode === countryCode) {
      baseStyles.fillColor = '#0074AD';
    } else if (!searchedMarketCode) {
      if (supportedMarketCodes.includes(countryCode)) {
        baseStyles.fillColor = '#93B99D';
      } else if (upcomingMarketCodes.includes(countryCode)) {
        baseStyles.fillColor = '#FFDDB5';
      }
    }

    switch (true) {
      case zoomLevel >= 20:
        baseStyles.strokeWidth = '0.05px';
        break;
      case zoomLevel >= 8:
        baseStyles.strokeWidth = '0.15px';
        break;
      case zoomLevel >= 7:
        baseStyles.strokeWidth = '0.20px';
        break;
      case zoomLevel >= 6:
        baseStyles.strokeWidth = '0.25px';
        break;
      case zoomLevel >= 5:
        baseStyles.strokeWidth = '0.30px';
        break;
    }

    if (SMALL_COUNTRY_CODES.includes(countryCode) || countryCode === 'UKR') {
      baseStyles.strokeWidth = '0.01px';
    }

    if (countryCode === 'MAR') {
      baseStyles.strokeWidth = '0px';
    }

    const style = {
      default: {
        fill: baseStyles.fillColor,
        outline: 'none',
        stroke: baseStyles.strokeColor,
        strokeWidth: baseStyles.strokeWidth
      },
      hover: {
        fill: baseStyles.fillColor,
        outline: 'none',
        stroke: baseStyles.strokeColor,
        strokeWidth: baseStyles.strokeWidth
      },
      pressed: {
        fill: baseStyles.fillColor,
        outline: 'none',
        stroke: baseStyles.strokeColor,
        strokeWidth: baseStyles.strokeWidth
      }
    };

    return style;
  };

  useEffect(() => {
    if (geoData) return;

    const geoJsonData = feature(worldmapData, worldmapData.objects.countries);
    setGeoData(geoJsonData);
  }, [center, zoomLevel, geoData]); // Focus map when it updates

  useEffect(() => {
    if (!geoData) return;

    const mapFeatures = (geoData as any).features.filter(
      (f) => f.properties.countryCode &&
        (f.properties.displayOnMap || 'Yes') === 'Yes'
    );
    const isFrench = i18n.language === 'fr';
    const getDisplayName = (f) => isFrench
      ? f.properties.nameFr || f.properties.name
      : f.properties.nameEn || f.properties.name;

    const searchableFeatures = mapFeatures.filter(
      (f) => (f.properties.availableInSearch || 'Yes') === 'Yes'
    );

    setCountries(searchableFeatures.map(getDisplayName).sort());
    setCountryCodeMapping(
      searchableFeatures.map((f) => ({
        [f.properties.countryCode]: getDisplayName(f)
      }))
    );
  }, [geoData, i18n.language]); // Rebuild search list/tooltip names on language change

  const getCountryNameByCode = (code: string) => {
    if (!code) return '';
    const mapping = countryCodeMapping.find((m) => Object.keys(m)[0] === code);
    if (mapping && mapping[code] && String(mapping[code]).trim() !== '') {
      return mapping[code];
    }
    // Fallback to the name embedded in the map's own geo data, which is
    // always available even before/if the external country-name API
    // fetch fails, is incomplete, or hasn't resolved yet.
    const geoFeature = geoData?.features.find(
      (f) => f.properties.countryCode === code
    );
    return geoFeature?.properties?.name || '';
  };

  // Prepare lists for accessibility
  const supportedCountryNames = (Array.from(
    new Set(supportedMarketCodes)
  ) as string[])
    .map(getCountryNameByCode)
    .filter((name) => !!name && name.trim() !== '')
    .sort();
  const upcomingCountryNames = (Array.from(
    new Set(upcomingMarketCodes)
  ) as string[])
    .map(getCountryNameByCode)
    .filter((name) => !!name && name.trim() !== '')
    .sort();
  const allCountryCodes = geoData?.features
    .filter((f) => (f.properties.displayOnMap || 'Yes') === 'Yes')
    .map((f) => f.properties.countryCode) || [];
  const unsupportedCountryCodes = allCountryCodes.filter(
    (code) => !supportedMarketCodes.includes(code) &&
      !upcomingMarketCodes.includes(code)
  );
  const unsupportedCountryNames = unsupportedCountryCodes
    .map(getCountryNameByCode)
    .filter((name) => !!name && name.trim() !== '')
    .sort();

  return (
    <Box className="supported-markets">


      <Box sx={{ display: 'flex', gap: '8px', maxWidth: '480px' }}>
        <Box aria-live='polite' sx={visuallyHidden} aria-atomic="true" role="status">
          {announcement}
        </Box>
        <AppAutocomplete
          itemList={countries}
          label={data.search_label}
          value={searchValue}
          input={searchValue}
          handleKey={handleKeyDown}
          onInputChange={handleInputChange}
          width={isMobile ? '100%' : '343px'}
          onChange={handleOnChangeAutocomplete} />
        <Button variant='primary' size='md' sx={{ whiteSpace: 'nowrap' }} onKeyDown={handleButtonKeyDown} onClick={handleClick}>{data.search}</Button>
      </Box>
      <Box>
        {searchValue !== '' && renderSearchText()}
      </Box>

      <Box
        sx={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
        aria-live="polite"
        aria-atomic="true"
      >
        {delayAccessibility ? (
          <>
            <Typography component="h2" tabIndex={0}>
              Supported countries
            </Typography>
            <ul>
              {supportedCountryNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
            <Typography component="h2" tabIndex={0}>
              Upcoming supported countries
            </Typography>
            <ul>
              {upcomingCountryNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
            <Typography component="h2" tabIndex={0}>
              Unsupported countries
            </Typography>
            <ul>
              {unsupportedCountryNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </>
        ) : null}
      </Box>
 
      <div className="map-container" tabIndex={-1} aria-hidden="true">
        <ComposableMap
          height={MAP_HEIGHT}
          projectionConfig={{ scale: 160 }}
          width={MAP_WIDTH}
          tabIndex={-1} // Allow focus but remove from tab order
          ref={mapRef} // Add ref to control focus programmatically
          focusable="false"
        >
          <ZoomableGroup
            center={center}
            maxZoom={MAX_ZOOM}
            translateExtent={[
              [0, 0],
              [MAP_WIDTH, MAP_HEIGHT]
            ]}
            zoom={zoomLevel}
            tabIndex={-1}
          >
            <Geographies geography={worldmapData} tabIndex={-1}>
              {({ geographies }) => geographies
                .filter((geo) => (geo.properties.displayOnMap || 'Yes') === 'Yes')
                .map((geo) => (
                  <AppTooltip
                    key={geo.rsmKey}
                    followCursor
                    title={renderTooltipTitle(geo)}
                  >
                    <g
                    >
                      <Geography
                        geography={geo}
                        style={styleFunction(geo.properties.countryCode)}
                        tabIndex={-1} />
                    </g>
                  </AppTooltip>
                ))}
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </div>
      <Box className="zoom-controls" aria-hidden="true">
        <Button
          className="zoom-button"
          aria-label="Zoom in"
          onClick={handleZoomIn}
          tabIndex={-1}
        >
          <AddIcon fontSize="small" />
        </Button>
        <Button
          className="zoom-button"
          aria-label="Zoom out"
          onClick={handleZoomOut}
          tabIndex={-1}
        >
          <RemoveIcon fontSize="small" />
        </Button>
      </Box>
      {searchValue === '' && (
        <>
          {data.supportedMarkets.market_types.map((marketType, i) => {
            const key = `marketType-${i}`;
            return (
              <Markets
                key={key}
                markets={marketType.markets.sort((a, b) => a.name.localeCompare(b.name)
                )}
                type={MARKET_TYPE.supported}
                tooltipSupportingText={data.supportedMarkets.supporting_text}
                subtitle={marketType.title}
                title={i === 0 ? data.supportedMarkets.title : undefined}
                groupTitle={data.supportedMarkets.title}
              />
            );
          })}
          <Markets
            markets={[...data.upcomingMarkets.markets].sort((a, b) => a.name.localeCompare(b.name)
            )}
            type={MARKET_TYPE.upcoming}
            tooltipSupportingText={data.upcomingMarkets.supporting_text}
            title={data.upcomingMarkets.title} />
        </>
      )}
    </Box>
  );
}
 
export default PointOfSaleCoverage;



